import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Alert,
    Stack,
    LinearProgress,
    CircularProgress,
    TablePagination,
    Tooltip,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Title, useGetIdentity } from 'react-admin';
import { supabase } from '../providers/supabase/supabase';
import { usePapaParse } from '../misc/usePapaParse';
import {
    useCropClassesImport,
    CropClassesCsvRow,
} from './useCropClassesImport';

type CropClassesRow = {
    id: number;
    crop: string;
    state: string;
    aip: string;
    class: string;
    created_at: string;
};

type SortField = 'crop' | 'state' | 'aip' | 'class';
type SortDir = 'asc' | 'desc';

export const CropClassesPage = () => {
    const { identity } = useGetIdentity();
    const isAdmin = !!(identity as any)?.administrator;

    const [rows, setRows] = useState<CropClassesRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('crop');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [cropFilter, setCropFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [snackMsg, setSnackMsg] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Supabase defaults to 1000 rows max per request.
            // Paginate to fetch all records.
            const PAGE_SIZE = 1000;
            let allData: CropClassesRow[] = [];
            let from = 0;
            let hasMore = true;

            while (hasMore) {
                const { data, error: fetchError } = await supabase
                    .from('crop_classes')
                    .select('*')
                    .order('crop', { ascending: true })
                    .order('state', { ascending: true })
                    .range(from, from + PAGE_SIZE - 1);

                if (fetchError) throw fetchError;
                const page = (data as CropClassesRow[]) ?? [];
                allData = allData.concat(page);
                hasMore = page.length === PAGE_SIZE;
                from += PAGE_SIZE;
            }

            setRows(allData);
        } catch (err: any) {
            console.error('Failed to fetch crop classes:', err);
            setError('Failed to load crop classes data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const uniqueCrops = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.crop)))
                .filter(Boolean)
                .sort(),
        [rows]
    );

    const uniqueStates = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.state)))
                .filter(Boolean)
                .sort(),
        [rows]
    );

    const displayRows = useMemo(() => {
        let filtered = rows;
        if (cropFilter) {
            filtered = filtered.filter(r => r.crop === cropFilter);
        }
        if (stateFilter) {
            filtered = filtered.filter(r => r.state === stateFilter);
        }

        return [...filtered].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            const cmp = String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rows, cropFilter, stateFilter, sortField, sortDir]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const handleClearAll = async () => {
        if (
            !window.confirm(
                'Are you sure you want to delete ALL crop class records? This cannot be undone.'
            )
        )
            return;

        try {
            const { error: delError } = await supabase
                .from('crop_classes')
                .delete()
                .neq('id', 0);
            if (delError) throw delError;
            setRows([]);
        } catch (err: any) {
            console.error('Failed to clear crop classes:', err);
            setError('Failed to clear crop classes data.');
        }
    };

    const handleDownloadSample = () => {
        const csv =
            'Crop,State,AIP,Class\n' +
            'Wheat,MT,Great American,A\n' +
            'Barley,ID,NAU,B\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'crop_classes_sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns: {
        field: SortField;
        label: string;
    }[] = [
        { field: 'crop', label: 'Crop' },
        { field: 'state', label: 'State' },
        { field: 'aip', label: 'AIP' },
        { field: 'class', label: 'Class' },
    ];

    const hasActiveFilters = cropFilter || stateFilter;

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Title title="Crop Classes" />

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Crop Classes
                </Typography>
                {isAdmin && (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Download sample CSV">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSample}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                        {rows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClearAll}
                            >
                                Clear All
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload CSV
                        </Button>
                    </Stack>
                )}
            </Stack>

            {/* Filter bar */}
            {rows.length > 0 && (
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <FilterIcon sx={{ color: 'text.secondary' }} />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Crop</InputLabel>
                        <Select
                            value={cropFilter}
                            label="Crop"
                            onChange={e => {
                                setCropFilter(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All Crops</em>
                            </MenuItem>
                            {uniqueCrops.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>State</InputLabel>
                        <Select
                            value={stateFilter}
                            label="State"
                            onChange={e => {
                                setStateFilter(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All States</em>
                            </MenuItem>
                            {uniqueStates.map(s => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {hasActiveFilters && (
                        <Stack direction="row" spacing={0.5}>
                            {cropFilter && (
                                <Chip
                                    label={`Crop: ${cropFilter}`}
                                    onDelete={() => {
                                        setCropFilter('');
                                        setPage(0);
                                    }}
                                    size="small"
                                />
                            )}
                            {stateFilter && (
                                <Chip
                                    label={`State: ${stateFilter}`}
                                    onDelete={() => {
                                        setStateFilter('');
                                        setPage(0);
                                    }}
                                    size="small"
                                />
                            )}
                        </Stack>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {displayRows.length} record
                        {displayRows.length !== 1 ? 's' : ''}
                    </Typography>
                </Stack>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <UploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        No crop classes data yet
                    </Typography>
                    {isAdmin ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Upload a CSV file to populate the crop classes
                                table.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload CSV
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">
                            An admin has not uploaded crop classes data yet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.field}
                                            sx={{
                                                fontWeight: 700,
                                            }}
                                            sortDirection={
                                                sortField === col.field
                                                    ? sortDir
                                                    : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortField === col.field}
                                                direction={
                                                    sortField === col.field
                                                        ? sortDir
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(col.field)
                                                }
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRows
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                {row.crop || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                {row.state || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                {row.aip || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                {row.class || '\u2014'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onComplete={() => {
                    setUploadOpen(false);
                    fetchData();
                }}
            />

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={2000}
                onClose={() => setSnackMsg('')}
                message={snackMsg}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            />
        </Box>
    );
};

// ---------- Upload Dialog ----------

function UploadDialog({
    open,
    onClose,
    onComplete,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}) {
    const { processBatch } = useCropClassesImport();
    const { importer, parseCsv, reset } = usePapaParse<CropClassesCsvRow>({
        batchSize: 50,
        processBatch,
    });

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length > 0) {
                parseCsv(accepted[0]);
            }
        },
        [parseCsv]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
        },
        multiple: false,
        disabled: importer.state === 'parsing' || importer.state === 'running',
    });

    const handleClose = () => {
        if (importer.state === 'complete') {
            onComplete();
        } else {
            onClose();
        }
        reset();
    };

    const progress =
        importer.state === 'running' || importer.state === 'complete'
            ? importer.rowCount > 0
                ? (importer.importCount / importer.rowCount) * 100
                : 0
            : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Crop Classes CSV</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Expected columns: <strong>Crop</strong>,{' '}
                        <strong>State</strong>, <strong>AIP</strong>,{' '}
                        <strong>Class</strong>
                    </Alert>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : '#ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor:
                                importer.state === 'running'
                                    ? 'default'
                                    : 'pointer',
                            bgcolor: isDragActive
                                ? 'action.hover'
                                : 'transparent',
                            transition: 'all 0.2s',
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon
                            sx={{
                                fontSize: 40,
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        />
                        <Typography>
                            {isDragActive
                                ? 'Drop the CSV file here...'
                                : 'Drag & drop a CSV file here, or click to select'}
                        </Typography>
                    </Box>

                    {importer.state === 'parsing' && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Parsing CSV...
                            </Typography>
                        </Stack>
                    )}

                    {(importer.state === 'running' ||
                        importer.state === 'complete') && (
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                            >
                                <Typography variant="body2">
                                    {importer.state === 'complete'
                                        ? 'Upload complete!'
                                        : `Importing ${importer.importCount} of ${importer.rowCount} rows...`}
                                </Typography>
                                {importer.remainingTime != null && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        ~
                                        {Math.ceil(
                                            importer.remainingTime / 1000
                                        )}
                                        s remaining
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                }}
                            />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="success.main"
                                >
                                    {'\u2713'} {importer.importCount} imported
                                </Typography>
                                {importer.errorCount > 0 && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                    >
                                        {'\u2717'} {importer.errorCount} errors
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {importer.state === 'error' && (
                        <Alert severity="error">
                            Failed to parse CSV: {importer.error.message}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {importer.state === 'complete' ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default CropClassesPage;
